import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/api/client';
import { Users, Plus, Trash2, UserPlus, CheckCircle2, X } from 'lucide-react';
import { toast } from 'sonner';

const REL = ['Husband','Wife','Daughter','Son','Brother','Sister','Father','Mother'];

export default function Family() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['family'], queryFn: async () => (await api.get('/member/family')).data });
  const { data: matches = [] } = useQuery({ queryKey: ['household-matches'], queryFn: async () => (await api.get('/member/household-matches')).data });

  const [form, setForm] = useState({ name: '', dob: '', relationship: 'Son' });

  const addMut = useMutation({
    mutationFn: async (p) => (await api.post('/member/family', p)).data,
    onSuccess: () => { toast.success('Family member added'); qc.invalidateQueries({ queryKey: ['family'] }); setForm({ name: '', dob: '', relationship: 'Son' }); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Could not add'),
  });

  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/member/family/${id}`)).data,
    onSuccess: () => { toast.success('Removed'); qc.invalidateQueries({ queryKey: ['family'] }); },
  });

  const reqLinkMut = useMutation({
    mutationFn: async (target) => (await api.post('/member/family-link/request', { target_profile_id: target })).data,
    onSuccess: () => { toast.success('Link request sent. Awaiting acceptance.'); qc.invalidateQueries({ queryKey: ['family'] }); qc.invalidateQueries({ queryKey: ['household-matches'] }); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Could not send'),
  });

  const acceptMut = useMutation({
    mutationFn: async (id) => (await api.post(`/member/family-link/${id}/accept`)).data,
    onSuccess: () => { toast.success('Family link accepted'); qc.invalidateQueries({ queryKey: ['family'] }); },
  });

  const rejectMut = useMutation({
    mutationFn: async (id) => (await api.post(`/member/family-link/${id}/reject`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['family'] }); },
  });

  if (isLoading) return <div>Loading…</div>;

  return (
    <div className="space-y-6">
      <div className="card-moj p-6">
        <h1 className="font-serif text-2xl text-moj-purple-deep flex items-center gap-2 mb-2"><Users/> Family Members</h1>
        <p className="text-sm text-moj-charcoal/70">Add your immediate family. If another approved member shares your Eircode, we’ll suggest a household link — details are only shared after both parties accept.</p>
      </div>

      {data?.pending_incoming?.length > 0 && (
        <div className="card-moj p-5 border-l-4 border-moj-gold">
          <h3 className="font-serif text-lg text-moj-purple-deep mb-3">Household link requests</h3>
          <div className="space-y-3">
            {data.pending_incoming.map((l) => (
              <div key={l.id} className="flex items-center justify-between gap-3">
                <div className="text-sm">A household member is requesting to link with you ({l.relationship || 'family'})</div>
                <div className="flex gap-2">
                  <button onClick={() => acceptMut.mutate(l.id)} className="btn-primary text-sm py-1.5 px-3" data-testid={`accept-link-${l.id}`}><CheckCircle2 size={14}/> Accept</button>
                  <button onClick={() => rejectMut.mutate(l.id)} className="btn-outline text-sm py-1.5 px-3"><X size={14}/> Decline</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {matches?.length > 0 && (
        <div className="card-moj p-5 border-l-4 border-moj-teal">
          <h3 className="font-serif text-lg text-moj-purple-deep mb-2">Possible household member found</h3>
          <p className="text-xs text-moj-charcoal/70 mb-3">We noticed another approved member at your Eircode. Send a link request — their name and family details are only shared after they accept.</p>
          <div className="space-y-3">
            {matches.map((m) => (
              <div key={m.profile_id} className="flex items-center justify-between">
                <div className="text-sm"><strong>{m.masked_name}</strong> • same Eircode</div>
                <button onClick={() => reqLinkMut.mutate(m.profile_id)} className="btn-gold text-sm py-1.5 px-3" data-testid={`send-link-${m.profile_id}`}><UserPlus size={14}/> Send link request</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card-moj p-6">
        <h3 className="font-serif text-lg text-moj-purple-deep mb-4">Add a family member</h3>
        <form onSubmit={(e) => { e.preventDefault(); if (!form.name) return toast.error('Please enter a name'); addMut.mutate(form); }} className="grid sm:grid-cols-4 gap-3 items-end" data-testid="family-form">
          <div><label className="label-moj">Name</label><input className="input-moj" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}/></div>
          <div><label className="label-moj">DOB</label><input type="date" className="input-moj" value={form.dob} onChange={(e) => setForm({...form, dob: e.target.value})}/></div>
          <div><label className="label-moj">Relationship</label>
            <select className="input-moj" value={form.relationship} onChange={(e) => setForm({...form, relationship: e.target.value})}>
              {REL.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <button type="submit" disabled={addMut.isPending} className="btn-primary justify-center" data-testid="family-add"><Plus size={16}/> Add</button>
        </form>
      </div>

      {data?.manual_members?.length > 0 && (
        <div className="card-moj p-6">
          <h3 className="font-serif text-lg text-moj-purple-deep mb-3">My Family</h3>
          <div className="divide-y">
            {data.manual_members.map((m) => (
              <div key={m.id} className="flex items-center justify-between py-3">
                <div>
                  <div className="font-semibold text-moj-purple-deep">{m.name} <span className="text-xs font-normal text-moj-gold-deep">({m.relationship})</span></div>
                  {m.dob && <div className="text-xs text-moj-charcoal/60">Born {m.dob}</div>}
                </div>
                <button onClick={() => delMut.mutate(m.id)} className="text-red-600 hover:bg-red-50 p-2 rounded-lg" aria-label={`Remove ${m.name}`}><Trash2 size={16}/></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {data?.linked_household_members?.length > 0 && (
        <div className="card-moj p-6">
          <h3 className="font-serif text-lg text-moj-purple-deep mb-3">Linked Household</h3>
          <div className="divide-y">
            {data.linked_household_members.map((m) => (
              <div key={m.link_id} className="py-3">
                <div className="font-semibold text-moj-purple-deep">{m.full_name} <span className="text-xs font-normal text-moj-gold-deep">({m.relationship || 'household'})</span></div>
                {m.dob && <div className="text-xs text-moj-charcoal/60">Born {m.dob}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
